/**
 * Wishlist Component
 * 
 * This component displays the user's wishlist in a modal overlay,
 * allowing users to view saved items, remove them, or add them to cart.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ShoppingCart } from 'lucide-react';
import { useWishlist } from '../../contexts/WishlistContext';
import { useCart } from '../../contexts/CartContext';
import './Wishlist.css';

/**
 * Wishlist component for displaying and managing wishlist items
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the wishlist modal is open
 * @param {Function} props.onClose - Function to close the wishlist modal
 */
const Wishlist = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { wishlistItems, removeFromWishlist, loading } = useWishlist();
  const { addToCart } = useCart();

  /**
   * Handles clicking on a product to navigate to its detail page
   * @param {string} productId - ID of the clicked product
   */
  const handleProductClick = (productId) => {
    onClose();
    navigate(`/product/${productId}`);
  };

  /**
   * Adds a wishlist product to the shopping cart
   * @param {Object} product - The product to add to cart
   */
  const handleAddToCart = async (product) => {
    await addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.main_image,
      qty_instock: product.qty_instock,
      quantity: 1
    }, 1);
  };

  // Don't render anything if the wishlist is not open
  if (!isOpen) return null;

  return (
    <div className="wishlist-overlay">
      <div className="wishlist-modal">
        {/* Wishlist header with title and close button */}
        <div className="wishlist-header">
          <h2>My Wishlist</h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Wishlist content area */}
        <div className="wishlist-content">
          {loading ? (
            // Loading state
            <div className="wishlist-loading">Loading wishlist...</div>
          ) : wishlistItems.length === 0 ? (
            // Empty wishlist state
            <div className="wishlist-empty">
              <p>Your wishlist is empty</p>
            </div>
          ) : (
            // Wishlist items list
            <div className="wishlist-items">
              {wishlistItems.map(product => (
                <div key={product.id} className="wishlist-item">
                  {/* Product image (clickable to go to product page) */}
                  <img
                    src={product.image || product.main_image}
                    alt={product.name}
                    onClick={() => handleProductClick(product.id)}
                    className="wishlist-item-image"
                  />
                  <div className="wishlist-item-info">
                    {/* Product name (clickable to go to product page) */}
                    <h3 onClick={() => handleProductClick(product.id)}>
                      {product.name}
                    </h3>
                    <p className="wishlist-item-price">${product.price}</p>
                    {/* Action buttons */}
                    <div className="wishlist-item-actions">
                      <button
                        className="remove-button"
                        onClick={() => removeFromWishlist(product.id)}
                      >
                        Remove
                      </button>
                      <button
                        className="add-to-cart-button"
                        onClick={() => handleAddToCart(product)}
                        disabled={product.qty_instock === 0}
                      >
                        <ShoppingCart size={16} />
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Wishlist;
